/**
 * AQ-Cloud2 public/auth shell — header + centered content + real footer.
 *
 * Used by pages reachable without authentication:
 *   • LoginPage
 *   • UnauthorizedPage
 *   • AuthCallbackPage
 *   • Registration pages
 *
 * The shell deliberately doesn't pull the main `Sidebar` or `AppShell`
 * because authenticated chrome would leak tenant-scoped UI to unauthed
 * users. See design-system/MASTER.md §12.
 */
import React from 'react'
import { Link } from 'react-router-dom'
import { BrandLockup } from '@/components/layout/BrandLockup'
import { Footer } from '@/components/layout/Footer'

interface AuthShellProps {
  children: React.ReactNode
  /** Right-aligned header nav items (e.g. "Docs", "Support", "Register"). */
  topNav?: React.ReactNode
  /** Build/version string passed through to the footer. */
  version?: string
  className?: string
  /**
   * `center` (default) — narrow, vertically centered content, for short
   * forms like login/registration. `start` — top-aligned, full-width
   * content for long-form pages (e.g. documentation).
   */
  align?: 'center' | 'start'
}

const DefaultTopNav = (): JSX.Element => (
  <nav className="flex items-center gap-4 text-sm text-muted-foreground" aria-label="Public">
    <a
      href="https://autoquotes-fes.com/aq-cpq/"
      target="_blank"
      rel="noreferrer noopener"
      className="hover:text-foreground transition-colors duration-fast"
    >
      Docs
    </a>
    <a
      href="https://support.revalizesoftware.com/"
      target="_blank"
      rel="noreferrer noopener"
      className="hover:text-foreground transition-colors duration-fast"
    >
      Support
    </a>
    <Link
      to="/register"
      className="h-9 px-3 rounded border border-border text-foreground hover:bg-muted inline-flex items-center transition-colors duration-fast"
    >
      Register
    </Link>
  </nav>
)

export const AuthShell = ({
  children,
  topNav,
  version,
  className,
  align = 'center',
}: AuthShellProps): JSX.Element => {
  return (
    <div className={`min-h-screen bg-background text-foreground flex flex-col ${className ?? ''}`}>
      <header className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandLockup size="md" />
          {topNav ?? <DefaultTopNav />}
        </div>
      </header>

      <main
        className={
          align === 'start'
            ? 'flex-1 p-4 sm:p-6'
            : 'flex-1 flex items-center justify-center p-6'
        }
      >
        {children}
      </main>

      <Footer {...(version ? { version } : {})} />
    </div>
  )
}
